import type {
  RumEvent,
  RumEventDomainContext,
  RumFetchResourceEventDomainContext,
} from '@datadog/browser-rum';

const getHeader = (
  headers: Headers | Record<string, string> | Array<[string, string]> | undefined,
  key: string
): string | undefined => {
  if (!headers) {
    return undefined;
  }

  if (headers instanceof Headers) {
    return headers.get(key) ?? 'unknown';
  }

  if (Array.isArray(headers)) {
    return headers.find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];
  }

  if (typeof headers === 'object') {
    const entries = Object.entries(headers);
    return entries.find(([k]) => k.toLowerCase() === key.toLowerCase())?.[1];
  }

  return 'unknown';
};

export function apolloResourceBeforeSend(
  event: RumEvent,
  context: RumEventDomainContext
): boolean | undefined {
  if (event.type !== 'resource' || !event.resource?.url?.endsWith('/graphql')) {
    return;
  }

  const { requestInit } = context as RumFetchResourceEventDomainContext;
  if (!requestInit) {
    return;
  }

  const { body, headers } = requestInit as {
    body?: any;
    headers?: Headers | Record<string, string> | Array<[string, string]>;
  };

  if (!body) {
    return;
  }

  let operationName: string | undefined;

  try {
    const bodyJson = typeof body === 'string' ? JSON.parse(body) : body;
    operationName = bodyJson?.operationName;
  } catch {
    // ignore parse errors, just don't set apollo context
  }

  const domain = getHeader(headers, 'x-gql-domain');
  const fetchPolicy = getHeader(headers, 'x-gql-fetch-policy') ?? 'cache-first';
  const nextFetchPolicy = getHeader(headers, 'x-gql-next-fetch-policy') ?? 'cache-first';
  const component = getHeader(headers, 'x-gql-component');

  const existingContext = event.context ?? {};
  const existingTelemetry = existingContext.telemetry ?? {};

  event.context = {
    ...existingContext,
    apollo: {
      ...existingTelemetry,
      operationName,
      domain,
      fetchPolicy,
      nextFetchPolicy,
      component: component === 'unknown' ? undefined : component,
    },
  };
}
